import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  HOME_TOUR_COMPLETED,
  HOME_TOUR_DISMISSED,
  HOME_TOUR_PROMPT_SHOWN,
  HOME_TOUR_SKIPPED,
  HOME_TOUR_STARTED,
  HOME_TOUR_STEP_VIEWED,
} from '@core/constants/analytics';
import type { TourSource } from '@app/shared/tour';
import type { HomeViewRole } from '@app/constants/home';

const ITEM_TYPE = 'HomeTour';

export type HomeTourSource = TourSource;

/** Avo `navigationSource` allowed values for home tour events. */
type HomeTourNavigationSource = 'auto' | 'manual';

type TourStepProps = {
  stepId?: string;
  stepIndex?: number;
  totalSteps?: number;
  source?: TourSource;
  role: HomeViewRole;
};

const toNavigationSource = (source: TourSource): HomeTourNavigationSource =>
  source === 'replay' ? 'manual' : 'auto';

/** Driver.js is 0-based; Avo expects 1-based stepIndex. */
const toAnalyticsStepIndex = (stepIndex?: number) =>
  typeof stepIndex === 'number' ? stepIndex + 1 : stepIndex;

export const trackHomeTourPromptShown = (role: HomeViewRole) => {
  analyticsTrackEvent(HOME_TOUR_PROMPT_SHOWN, { itemType: ITEM_TYPE, role });
};

export const trackHomeTourDismissed = (role: HomeViewRole) => {
  analyticsTrackEvent(HOME_TOUR_DISMISSED, { itemType: ITEM_TYPE, role });
};

export const trackHomeTourStarted = (
  source: HomeTourSource,
  role: HomeViewRole
) => {
  analyticsTrackEvent(HOME_TOUR_STARTED, {
    itemType: ITEM_TYPE,
    navigationSource: toNavigationSource(source),
    role,
  });
};

export const trackHomeTourStepViewed = ({
  stepId,
  stepIndex,
  totalSteps,
  source = 'first_visit',
  role,
}: TourStepProps) => {
  analyticsTrackEvent(HOME_TOUR_STEP_VIEWED, {
    itemType: ITEM_TYPE,
    itemId: stepId,
    stepIndex: toAnalyticsStepIndex(stepIndex),
    totalSteps,
    navigationSource: toNavigationSource(source),
    role,
  });
};

export const trackHomeTourCompleted = ({
  stepIndex,
  totalSteps,
  source = 'first_visit',
  role,
}: TourStepProps) => {
  analyticsTrackEvent(HOME_TOUR_COMPLETED, {
    itemType: ITEM_TYPE,
    stepIndex: toAnalyticsStepIndex(stepIndex),
    totalSteps,
    navigationSource: toNavigationSource(source),
    role,
  });
};

export const trackHomeTourSkipped = ({
  stepId,
  stepIndex,
  totalSteps,
  source = 'first_visit',
  role,
}: TourStepProps) => {
  analyticsTrackEvent(HOME_TOUR_SKIPPED, {
    itemType: ITEM_TYPE,
    itemId: stepId,
    stepIndex: toAnalyticsStepIndex(stepIndex),
    totalSteps,
    navigationSource: toNavigationSource(source),
    role,
  });
};
